import React, { useState } from "react";
import { INK, MUTED, PAPER, GRID, W, M, axis, scale, plural, spread, Halo, Legend, Tooltip, Axes } from "./clusterKit";

// ---- silhouette_by_k: average silhouette width for each number of clusters, real data against structureless data ----
// Props (ChartData#silhouette_by_k): ks [numbers of clusters], silhouette [average per k], reference [structureless median per k],
// referenceLabel, chosen (the k the page keeps), referenceMax (the largest structureless value at the chosen k),
// normal, normalMax (a second baseline at the chosen k: median and largest); draws (settings) = data sets behind each median.
const bare = (v, places = 2) => (v < 0 ? "−" : "") + Math.abs(v).toFixed(places).replace(/^0(?=\.)/, "");
const tick = (t) => (t === 0 ? "0" : bare(t, 1));
const NUMBERS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const word = (n) => NUMBERS[n] || String(n);
const Word = (n) => word(n).charAt(0).toUpperCase() + word(n).slice(1);

export default function SilhouetteByKChart({ noun = "participant", ks, silhouette, reference, chosen, referenceMax, normal, normalMax, draws }) {
	const [hover, setHover] = useState(null);
	const height = 300, box = { l: 44, r: W - 170, t: M.top + 22, b: height - 44 };
	const axY = axis([0, ...silhouette, ...(reference || []), normalMax || 0], 0.08);
	const axX = { min: ks[0] - 0.5, max: ks[ks.length - 1] + 0.5, ticks: ks };
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const path = (vs) => vs.map((v, i) => `${i ? "L" : "M"}${x(ks[i])},${y(v)}`).join(" ");
	const half = (x(ks[1]) - x(ks[0])) / 2;
	const at = ks.indexOf(chosen), last = ks.length - 1;
	const many = draws ? `${draws} ` : "";
	const ends = spread([
		{ key: "real", y: y(silhouette[last]) + 4, text: "Real data", color: INK },
		...(reference ? [{ key: "ref", y: y(reference[last]) + 4, text: "Shuffled data, median", color: MUTED }] : [])
	], 14);
	const gaps = reference ? silhouette.map((s, i) => s - reference[i]) : [];
	const widest = gaps.length ? ks[gaps.indexOf(Math.max(...gaps))] : null;
	const lo = reference && Math.min(...reference), hi = reference && Math.max(...reference);
	const tip = (i) => [plural(ks[i], "cluster"), `Real data: ${bare(silhouette[i])}`,
		...(reference ? [`Shuffled data, median: ${bare(reference[i])}`] : []),
		...(ks[i] === chosen && referenceMax != null ? [`Shuffled data, largest: ${bare(referenceMax)}`] : []),
		...(ks[i] === chosen && normal != null ? [`One normal cloud, median: ${bare(normal)}${normalMax != null ? `, largest ${bare(normalMax)}` : ""}`] : [])];
	return (
		<figure>
			<Legend items={[{ label: "Real data", color: INK, kind: "line" },
				...(reference ? [{ label: `Shuffled data: each scale shuffled on its own (median of ${many}data sets)`, color: MUTED, kind: "dash" }] : []),
				...(normal != null ? [{ label: "One normal cloud with the scales' correlations", color: MUTED, kind: "ring" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Average silhouette width for ${ks[0]} to ${ks[last]} clusters: ${ks.map((k, i) => `${k}, ${bare(silhouette[i])}`).join("; ")}. ${chosen} clusters are kept.${reference ? ` Shuffled data give medians of ${reference.map((v) => bare(v)).join(", ")}.` : ""}${normal != null ? ` One normal cloud with the scales' correlations gives ${bare(normal)} for ${chosen} clusters.` : ""}`}>
				{at >= 0 && (
					<g pointerEvents="none">
						<rect x={x(chosen) - half} y={box.t} width={2 * half} height={box.b - box.t} fill={GRID} opacity="0.5" />
						<text x={x(chosen)} y={box.b - 6} fontSize="11" fill={INK} fontWeight="600" textAnchor="middle">Kept: {chosen}</text>
					</g>
				)}
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel="Number of clusters (k)" yLabel="Average silhouette width (s̄)" yFormat={tick} />
				{reference && <path d={path(reference)} fill="none" stroke={MUTED} strokeWidth="2" strokeDasharray="6 4" />}
				{reference && reference.map((v, i) => <circle key={`r${i}`} cx={x(ks[i])} cy={y(v)} r="3.5" fill={MUTED} pointerEvents="none" />)}
				<path d={path(silhouette)} fill="none" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
				{silhouette.map((v, i) => (
					<g key={`s${i}`} pointerEvents="none">
						<circle cx={x(ks[i])} cy={y(v)} r={ks[i] === chosen ? 6.5 : 5} fill={INK} stroke={PAPER} strokeWidth="1.5" />
						<Halo x={x(ks[i])} y={y(v) - 11} fill={INK} fontWeight={ks[i] === chosen ? 700 : 400} textAnchor="middle">{bare(v)}</Halo>
					</g>
				))}
				{normal != null && at >= 0 && (
					<g pointerEvents="none">
						<circle cx={x(chosen)} cy={y(normal)} r="5" fill={PAPER} stroke={INK} strokeWidth="1.5" />
						<Halo x={x(chosen) - 9} y={y(normal) - 7} fill={MUTED} textAnchor="end">Normal cloud {bare(normal)}</Halo>
					</g>
				)}
				{ends.map((e) => <text key={e.key} x={box.r + 10} y={e.y} fontSize="11" fill={e.color}>{e.text}</text>)}
				{ks.map((k, i) => (
					<rect key={`hit${k}`} x={x(k) - half} y={box.t} width={2 * half} height={box.b - box.t} fill="transparent"
						onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
				))}
				{hover !== null && <Tooltip x={x(ks[hover])} lines={tip(hover)} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each point is one solution, Ward's tree cut at k clusters and refined by k-means, scored by its average silhouette width: higher means {noun}s sit more clearly in their own cluster. {Word(chosen)} clusters score highest, {bare(silhouette[at])}.
				{reference ? ` The dashed line is the same procedure on shuffled data, every score kept but the profiles broken up: ${bare(lo)} to ${bare(hi)}, so clustering finds some separation even where there are no profiles. What counts is how far the real line sits above it${widest === chosen ? `, and the gap is widest at ${word(chosen)}` : ""}.` : ""}
				{normal != null ? ` Shuffling also removes the correlations between the scales; one normal cloud that keeps them, with no subgroups, gives ${bare(normal)} for ${word(chosen)} clusters.` : ""}
			</figcaption>
		</figure>
	);
}
